import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Download as DownloadIcon,
  Upload,
  Users,
  Loader2,
  Trash2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Standalone page for managing the Family Mapping (Group Head ↔ PAN).
// Reachable from Masters → Profile → "Family Mapping Upload".
const FamilyMappingUpload = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await api.get("/family-mapping");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load mapping");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadCurrent = async () => {
    setDownloading(true);
    try {
      const baseUrl = api.defaults.baseURL;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/family-mapping/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `family_mapping_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Family mapping downloaded");
    } catch (e) {
      toast.error(e?.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/family-mapping/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const {
        total_rows,
        upserted,
        updated,
        skipped,
        skipped_missing_pan,
        skipped_missing_head,
        skipped_duplicate,
        duplicate_pan_samples,
      } = r.data;
      const parts = [];
      if (skipped_missing_pan) parts.push(`${skipped_missing_pan} missing PAN`);
      if (skipped_missing_head) parts.push(`${skipped_missing_head} missing Group Head`);
      if (skipped_duplicate) parts.push(`${skipped_duplicate} duplicate PAN`);
      const skipBreakdown = parts.length ? ` (${parts.join(", ")})` : "";
      const dupHint =
        skipped_duplicate && duplicate_pan_samples?.length
          ? ` — duplicates e.g. ${duplicate_pan_samples.slice(0, 3).join(", ")}`
          : "";
      toast.success(
        `${upserted + updated} rows applied · ${skipped ?? 0} skipped${skipBreakdown} (of ${total_rows})${dupHint}`,
        { duration: 8000 }
      );
      fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (pan) => {
    if (!window.confirm(`Remove family mapping for ${pan}?`)) return;
    try {
      await api.delete(`/family-mapping/${encodeURIComponent(pan)}`);
      toast.success(`Removed ${pan}`);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.pan || "").toLowerCase().includes(q) ||
      (r.name || "").toLowerCase().includes(q) ||
      (r.group_head_pan || "").toLowerCase().includes(q) ||
      (r.group_head_name || "").toLowerCase().includes(q)
    );
  });

  // Aggregate by group head for the summary stats
  const headsMap = {};
  for (const r of rows) {
    const k = r.group_head_pan || "—";
    if (!headsMap[k]) headsMap[k] = { name: r.group_head_name, count: 0 };
    headsMap[k].count += 1;
  }
  const headsCount = Object.keys(headsMap).length;
  const totalMembers = rows.length;

  return (
    <div className="container mx-auto px-4 py-8" data-testid="family-mapping-upload-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/masters")}
        className="mb-4"
        data-testid="back-to-masters-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Masters
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-700" />
            Family Mapping
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Upload an xlsx with 4 columns:{" "}
            <code className="rounded bg-slate-100 px-1">PAN Guardian</code>,{" "}
            <code className="rounded bg-slate-100 px-1">PAN Name</code>,{" "}
            <code className="rounded bg-slate-100 px-1">Group Head PAN</code>,{" "}
            <code className="rounded bg-slate-100 px-1">Group Head Name</code>.
            Each row maps a family member's PAN to their Group Head. Group
            heads see all family members listed under their portfolio;
            individuals continue to log in to their own portfolio normally.
          </p>

          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              {headsCount} group heads
            </Badge>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              {totalMembers} members mapped
            </Badge>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadCurrent}
              disabled={downloading || rows.length === 0}
              data-testid="download-current-mapping-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Downloading…
                </>
              ) : (
                <>
                  <DownloadIcon className="mr-2 h-4 w-4" />
                  Download current mapping
                </>
              )}
            </Button>
            <label>
              <input
                type="file"
                accept=".xlsx"
                hidden
                onChange={handleUpload}
                data-testid="family-mapping-file-input"
              />
              <Button
                variant="default"
                disabled={uploading}
                asChild
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <span>
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload xlsx
                    </>
                  )}
                </span>
              </Button>
            </label>
          </div>

          <div className="pt-2">
            <input
              type="text"
              placeholder="Search by PAN or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-sm rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="family-mapping-search"
            />
          </div>

          <div className="overflow-x-auto rounded border border-slate-100">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">PAN</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Group Head PAN</th>
                  <th className="px-3 py-2 text-left">Group Head Name</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                      <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center italic text-slate-400">
                      {rows.length === 0
                        ? "No family mapping uploaded yet."
                        : "No rows match the search."}
                    </td>
                  </tr>
                ) : (
                  filtered.slice(0, 500).map((r) => (
                    <tr
                      key={r.pan}
                      className={`border-t border-slate-100 ${
                        r.pan === r.group_head_pan ? "bg-amber-50/40" : ""
                      }`}
                      data-testid={`family-mapping-row-${r.pan}`}
                    >
                      <td className="px-3 py-1.5 font-mono text-xs">{r.pan}</td>
                      <td className="px-3 py-1.5">{r.name || "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">
                        {r.group_head_pan}
                        {r.pan === r.group_head_pan && (
                          <Badge className="ml-2 bg-amber-100 text-amber-800 border-0 text-[10px]">HEAD</Badge>
                        )}
                      </td>
                      <td className="px-3 py-1.5">{r.group_head_name || "—"}</td>
                      <td className="px-3 py-1.5 text-right">
                        <button
                          onClick={() => handleDelete(r.pan)}
                          className="rounded p-1 text-rose-500 hover:bg-rose-50"
                          title="Remove this mapping"
                          data-testid={`family-mapping-delete-${r.pan}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {filtered.length > 500 && (
              <div className="px-3 py-2 text-xs italic text-slate-400">
                Showing first 500 of {filtered.length} matching rows. Refine your search to see more.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FamilyMappingUpload;
